import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { connectState } from '@examdojo/angular/util';
import {
  CategoryService,
  PageType,
  PageVersionQuery,
  PageVersionService,
  PageVersionStoreModel,
  TopicStatusComponent,
} from '@examdojo/category/v2';
import { ButtonComponent } from '@examdojo/core/button';
import { DateTimeModule } from '@examdojo/core/date-time';
import { IconComponent } from '@examdojo/core/icon';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { Callout, TiptapTableOfContentsComponent } from '@examdojo/core/tiptap';
import { TiptapEditorWrapperComponent } from '@examdojo/core/tiptap-react-ui-components';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { ToastService } from '@examdojo/toast';
import { LoaderComponent } from '@examdojo/ui/loader';
import { SaveIndicatorComponent } from '@examdojo/ui/save-indicator';
import { getFormSavingStatus } from '@examdojo/ui/save-indicator/get-form-saving-status';
import { assertNonNullable } from '@examdojo/util/assert';
import { Editor } from '@tiptap/core';
import { combineLatest, debounceTime, distinctUntilChanged, EMPTY, filter, map, merge, switchMap, tap } from 'rxjs';
import { PageVersionBrowserDialogService } from './page-version-browser';

const SAVE_DEBOUNCE_MS = 1000;

@Component({
  selector: 'exam-topic-concept-tab',
  imports: [
    TiptapEditorWrapperComponent,
    TopicStatusComponent,
    LoaderComponent,
    DateTimeModule,
    MatTooltip,
    IconComponent,
    SaveIndicatorComponent,
    ButtonComponent,
    IconButtonComponent,
    TiptapTableOfContentsComponent,
  ],
  templateUrl: './topic-tab.component.html',
  styleUrl: './topic-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PageVersionBrowserDialogService],
})
export class TopicTabComponent implements OnInit {
  constructor(
    private readonly pageVersionService: PageVersionService,
    private readonly pageVersionQuery: PageVersionQuery,
    private readonly categoryService: CategoryService,
    private readonly pageVersionBrowserDialogService: PageVersionBrowserDialogService,
    private readonly destroyRef: DestroyRef,
    private readonly toastService: ToastService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly elementRef: ElementRef,
  ) {
    merge(
      this.activePageVersion$.pipe(
        map((page) => page?.content),
        filter((content) => content !== this.contentCtrl.value),
        tap((content) => {
          this.contentCtrl.setValue(content ?? '');
        }),
      ),
      this.saveVersionWhenContentChanges(),
    )
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  readonly scrollableContainer = signal<HTMLElement | undefined>(undefined);
  readonly pageType = input.required<PageType>();

  private readonly pageVersionIdOverride = signal<PageVersionStoreModel['id'] | null>(null);

  readonly activePageVersion$ = combineLatest([
    toObservable(this.pageType),
    toObservable(this.pageVersionIdOverride),
  ]).pipe(
    switchMap(([pageType, activePageVersionId]) => {
      if (activePageVersionId) {
        return this.pageVersionQuery
          .getPageVersionsOfType(pageType)
          .pipe(map((pageVersions) => pageVersions.find(({ id }) => id === activePageVersionId)));
      }

      return this.pageVersionQuery.getMainPageVersionForType(pageType);
    }),
  );

  readonly contentCtrl = new FormControl<string>('', { nonNullable: true });

  readonly state = connectState({
    activePageVersion: this.activePageVersion$,
    formSavingStatus: getFormSavingStatus(this.contentCtrl, SAVE_DEBOUNCE_MS),
  });

  // TODO: uncomment when we set up collaboration
  // readonly roomId = computed(() => {
  //   const pageType = this.pageType();
  //   const activeVersion = this.state.activePageVersion;
  //   return activeVersion ? `page-${pageType}-${activeVersion.id}` : `page-${pageType}`;
  // });

  protected editorOptions: Partial<Editor['options']> = {
    extensions: [Callout],
  };

  ngOnInit() {
    this.scrollableContainer.set(this.elementRef.nativeElement.closest('.main-content-area') as HTMLElement);
  }

  publish() {
    const version = this.state.activePageVersion;
    assertNonNullable(version, 'active page version');

    if (version.page_status === 'published') {
      return;
    }

    this.pageVersionService
      .publishVersion(version)
      .pipe(
        switchMap(() => this.categoryService.fetchTopicsTree()),
        this.errorHandlerService.catchHttpErrors(() => EMPTY),
        tap(() => {
          this.toastService.success(`Page published successfully`);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  createDraft() {
    const version = this.state.activePageVersion;
    assertNonNullable(version, 'active page version');

    if (version.page_status === 'draft') {
      return;
    }

    this.pageVersionService
      .createDraft({ ...version, content: this.contentCtrl.value })
      .pipe(
        this.errorHandlerService.catchHttpErrors(() => EMPTY),
        tap(() => {
          this.toastService.success(`Draft created`);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  async openVersionBrowserDialog() {
    const selectedVersionId = await this.pageVersionBrowserDialogService.openDialog({
      input: { pageType: this.pageType(), activeId: this.state.activePageVersion?.id },
    });

    if (selectedVersionId) {
      this.pageVersionIdOverride.set(selectedVersionId);
    }
  }

  private saveVersionWhenContentChanges() {
    return this.activePageVersion$.pipe(
      switchMap((version) =>
        this.contentCtrl.valueChanges.pipe(
          distinctUntilChanged(),
          debounceTime(SAVE_DEBOUNCE_MS),
          map((content) => [version, content] as const),
        ),
      ),
      filter(([version, content]) => !!version && content !== version.content),
      switchMap(([version, content]) => {
        assertNonNullable(version, 'active page version');
        return this.pageVersionService.update(version!.id, {
          content,
        });
      }),
      tap(() => this.contentCtrl.markAsPristine()),
    );
  }
}
